import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Container, IconButton, Modal, TextField, Typography } from '@mui/material'
import { makeStyles, styled } from '@mui/styles'
import { isValidEmail } from '@cbgms/base/utils/validations'
import CloseIcon from '@mui/icons-material/Close'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Roles } from '@cbgms/api'
import { useTranslation } from 'react-i18next'
import { Dispatch } from 'redux'
import { sendConfirmationEmail } from '../state/actions'

interface ApiResponse {
  Data: {
    Preconditions: {
      length: number
      Creditworthiness: string
      Threestepcustomer: string
      Deliveroptions: string
      Packageexists: string
    }
    Status: string
    AdminStatus: string
  }
}

interface customerFormDataType {
  CustNo: string
  ShipToLoc: string
  OuName: string
  AppId: number | undefined
  ModuleId: string
  OverriddenByAdmin: boolean
  SignUpEmailOfCustomer: string
  SalesRepEmail: string | null
  ModulePrice: number
  CustomerFirstName?: string | null
  CustomerLastName?: string | null
}

interface ConfirmationPopupProps {
  loggedUser: { role: Roles; email: string } | { role: null; email: null }
  confirmationPopUpOpen: boolean
  setConfirmationPopUpOpen: React.Dispatch<React.SetStateAction<boolean>>
  customerFormData: customerFormDataType | null
  SignUpEmailOfCustomer: string
  setSignUpEmailOfCustomer: React.Dispatch<React.SetStateAction<string>>
  preconditionStatus: ApiResponse | null
  bypasPopup: boolean
  linkSendButtionActive: boolean
  setLinkSendButtionActive: React.Dispatch<React.SetStateAction<boolean>>
  setDisableProceed: React.Dispatch<React.SetStateAction<boolean>>
  CustomerFirstName?: string | null
  CustomerLastName?: string | null
}

const useStyles = makeStyles({
  title: {
    marginTop: '0px',
    fontSize: 20,
    fontWeight: 700
  },
  emailInput: {
    width: 500,
    maxWidth: '100%'
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '8px'
  },
  modalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    backgroundColor: 'white',
    boxShadow: '24',
    padding: '16px',
    borderRadius: '8px'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid',
    borderColor: 'divider',
    paddingBottom: '8px'
  },
  modalContent: {
    marginTop: '16px'
  },
  modalDivider: {
    borderBottom: '1px solid',
    borderColor: 'divider',
    marginY: '16px'
  },
  modalActions: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  cancelButton: {
    backgroundColor: 'white',
    color: 'blue',
    marginRight: '8px',
    border: '1px solid blue',
    '&:hover': {
      backgroundColor: 'white',
      color: 'blue'
    }
  },
  proceedButton: {
    backgroundColor: 'blue',
    color: 'white'
  },
  errorModalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    backgroundColor: 'white',
    boxShadow: '24',
    padding: '16px',
    borderRadius: '8px'
  },
  errorModalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid',
    borderColor: 'divider',
    paddingBottom: '8px'
  },
  errorModalContent: {
    marginTop: '16px'
  },
  errorModalActions: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  okButton: {
    backgroundColor: 'Orange',
    color: 'white'
  }
})

const StyledContainer = styled(Container)(() => ({
  marginTop: '20px'
}))

const StyledBox = styled(Box)(({ theme }) => ({
  gap: 4,
  padding: theme.spacing(2)
}))

const InfoText = styled(Typography)(() => ({
  marginTop: '10px',
  marginBottom: '20px'
}))

const LabelBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(0),
  marginLeft: '30px'
}))

const BoldLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginRight: theme.spacing(2)
}))

enum OrderStatus {
  Success = 'Success',
  Fail = 'Fail'
}

function ConfirmationPage(props: ConfirmationPopupProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch<Dispatch>()
  const preConditionLists = props.preconditionStatus?.Data.Preconditions
  const preConditionStatus = props.preconditionStatus?.Data.Status
  const [loading, setLoading] = useState<boolean>(false)
  const [createOrderResponse, setCreateOrderResponse] = useState<{ Status: string; Message: string } | null>(null)
  const [isError, setIsError] = useState(false)
  const [errorEmailMessage, setErrorEmailMessage] = useState(t('AppLabels.errorSendingEmail'))
  const [errorPopup, setErrorPopup] = useState(true)
  const [emailError, setEmailError] = useState('')
  const [specialPopup, SetSpecialPopup] = useState(false)
  const [customerEmail, setCustomerEmail] = useState(props.SignUpEmailOfCustomer)
  const [customerFirstName, setCustomerFirstName] = useState<string>()
  const [customerLastName, setCustomerLastName] = useState<string>()

  const history = useHistory()
  const classes = useStyles()

  const handleClose = () => {
    props.setConfirmationPopUpOpen(false)
    setErrorPopup(false)
    setIsError(false)
  }

  const errorMessages = {
    Creditworthiness: t('AppLabels.creditWorthinessNotOk'),
    Threestepcustomer: t('AppLabels.threeStepCustomerNotOk'),
    Deliveroptions: t('AppLabels.deliveryOptionsNotUpdated'),
    Packageexists: t('AppLabels.packageAlreadySubscribed')
  }

  const handleCreateOrder = async () => {
    setLoading(true)
    const orderRequestBody = props.customerFormData
    if (customerFirstName || customerLastName) {
      orderRequestBody!.CustomerFirstName = customerFirstName || ''
      orderRequestBody!.CustomerLastName = customerLastName || ''
    }

    try {
      const response = await dispatch(sendConfirmationEmail(orderRequestBody) as any)
      setLoading(false)
      const { Data, responseError } = response

      if (responseError) {
        if (responseError === t('AppLabels.emailAlreadyExists')) {
          setErrorEmailMessage(t('AppLabels.emailAlreadyInUse'))
        } else {
          setErrorEmailMessage(t('AppLabels.emailSendingError'))
          setCreateOrderResponse({
            Status: 'responseError',
            Message: t('AppLabels.emailSendingErrorMessage')
          })
        }
        setIsError(true)
      } else {
        setCreateOrderResponse(Data)
      }
    } catch (error) {
      console.error('Error:', error)
      setLoading(false)
      setIsError(true)
      if ((error as any).response) {
        const status = (error as any).response.status

        if (status === 502) {
          SetSpecialPopup(false)
        }
      } else {
        SetSpecialPopup(true)
      }
    }
  }

  useEffect(() => {
    if (props.bypasPopup && props.SignUpEmailOfCustomer && props.linkSendButtionActive) {
      handleCreateOrder()
      props.setLinkSendButtionActive(false)
    } else {
      props.setDisableProceed(false)
    }
  }, [props.bypasPopup, props.SignUpEmailOfCustomer, props.linkSendButtionActive, customerEmail])

  useEffect(() => {
    isError && setErrorPopup(true)
  }, [isError])

  useEffect(() => {
    if (createOrderResponse?.Status === OrderStatus.Success) {
      props.setConfirmationPopUpOpen(false)
      history.push('/LKQ/OfferPackage/form/success')
    }
  }, [createOrderResponse, history, props])

  useEffect(() => {
    let disableProceed = false

    // Check if customerFirstName and customerLastName are valid
    if (!customerFirstName || !customerLastName) {
      disableProceed = true
    }

    // Check if customerEmail is valid
    if (!customerEmail || isValidEmail(customerEmail) !== undefined) {
      disableProceed = true
      setEmailError(!customerEmail ? t('AppLabels.requiredField') : t('AppLabels.emailMustBeValid'))
    } else {
      setEmailError('')
    }

    // Update the disableProceed state
    props.setDisableProceed(disableProceed)
  }, [customerFirstName, customerLastName, customerEmail, props, t])

  const handleEmailChange = (e: { target: { value: string } }) => {
    const email = e.target.value
    setCustomerEmail(email)
    props.setSignUpEmailOfCustomer(email)
  }
  const handleFirstNameChange = (e: { target: { value: string } }) => {
    const firstName = e.target.value
    setCustomerFirstName(firstName)
  }
  const handleLastNameChange = (e: { target: { value: string } }) => {
    const lastName = e.target.value
    setCustomerLastName(lastName)
  }

  return (
    <StyledContainer>
      <StyledBox>
        <Typography variant='h6' className={classes.title}>
          {t('Customers.Confirmation')}
        </Typography>
        <InfoText variant='body1'>{t('Customers.confirmationMessage')}</InfoText>
        <LabelBox>
          <BoldLabel>{t('AppLabels.emailEmployee')}:</BoldLabel>
          <TextField
            id='outlined-read-only-input'
            InputProps={{
              readOnly: true
            }}
            label={t('AppLabels.email')}
            variant='outlined'
            data-testid='customer-email-input'
            size='small'
            defaultValue={props.loggedUser.email}
            className={classes.emailInput}
          />
        </LabelBox>
        <br />
        <LabelBox>
          <BoldLabel>{t('AppLabels.contactCustomer')}:</BoldLabel>
          <TextField
            id='customer-firstname-input'
            InputLabelProps={{
              required: true
            }}
            label={t('AppLabels.firstname')}
            variant='outlined'
            data-testid='customer-firstname-input'
            size='small'
            fullWidth
            style={{ marginLeft: '-11px' }}
            error={!customerFirstName}
            helperText={!customerFirstName ? t('AppLabels.requiredField') : ''}
            className={classes.emailInput}
            onChange={handleFirstNameChange}
          />
        </LabelBox>
        <br />
        <div style={{ marginLeft: '170px' }}>
          <TextField
            id='customer-lastname-input'
            InputLabelProps={{
              required: true
            }}
            label={t('AppLabels.lastname')}
            variant='outlined'
            data-testid='customer-lastname-input'
            size='small'
            fullWidth
            error={!customerLastName}
            helperText={!customerLastName ? t('AppLabels.requiredField') : ''}
            className={classes.emailInput}
            onChange={handleLastNameChange}
          />
        </div>
        <br />
        <LabelBox>
          <BoldLabel>{t('AppLabels.emailCustomer')}:</BoldLabel>
          <TextField
            InputLabelProps={{
              required: true
            }}
            id='customer-email'
            data-testid='customer-email-textfield'
            label={t('AppLabels.customerEmail')}
            variant='outlined'
            size='small'
            value={props.SignUpEmailOfCustomer}
            className={classes.emailInput}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError}
          />
        </LabelBox>
      </StyledBox>
      {loading && (
        <Box className={classes.loadingBox}>
          <CircularProgress size={20} />
        </Box>
      )}
      {!isError && !loading && (
        <Modal open={props.confirmationPopUpOpen} onClose={handleClose}>
          <Box className={classes.modalBox}>
            <Box className={classes.modalHeader}>
              <Typography variant='h6' component='h2'>
                {t('AppLabels.confirmationRequired')}
              </Typography>
              <IconButton onClick={handleClose} data-testid='close-button'>
                <CloseIcon />
              </IconButton>
            </Box>
            {preConditionStatus !== OrderStatus.Success &&
              preConditionLists &&
              Object.entries(preConditionLists).map(([key, value]) => {
                if (value === 'Fail') {
                  return (
                    <Alert key={key} severity='warning' sx={{ mt: 2 }}>
                      {errorMessages[key as keyof typeof errorMessages]}
                    </Alert>
                  )
                }
                return null
              })}

            <Typography className={classes.modalContent}>{t('AppLabels.continueOrderingProcess')}</Typography>

            <Box className={classes.modalDivider} />

            <Box className={classes.modalActions}>
              <Button className={classes.cancelButton} onClick={handleClose}>
                {t('AppLabels.cancel')}
              </Button>

              <Button variant='contained' className={classes.proceedButton} onClick={handleCreateOrder}>
                {t('AppLabels.proceed')}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      {isError && !loading && specialPopup && (
        <Modal open={errorPopup} onClose={handleClose}>
          <Box className={classes.errorModalBox}>
            <Box className={classes.errorModalHeader}>
              <Typography variant='h6' component='h2'>
                {t('AppLabels.attention')}
              </Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography className={classes.errorModalContent}>{errorEmailMessage}</Typography>
            <Box className={classes.errorModalActions}>
              <Button variant='contained' className={classes.okButton} type='button' onClick={handleClose}>
                {t('AppLabels.ok')}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </StyledContainer>
  )
}

export default ConfirmationPage
